/**
 * @module  core/aem
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */
// Core Libraries
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
// Services
import { ContentProviderService } from '@core/aem/services/content-provider.service';
// Interfaces
import { ConfigData } from '@experience/repdash/interfaces/aem-content.interface';
import { ImageGalleryData } from './models/image-gallery.interface';
import { CONTENT_ENDPOINT } from '@core/aem/content-endpoints';
import { ContainerData } from '@core/aem/components/container/models/container-data.interface';
import { ContentItem } from '@core/aem/models/content-item.interface';

@Component({
  selector: 'tmo-image-gallery',
  templateUrl: './image-gallery.component.html',
  styleUrls: [],
})
export class ImageGalleryComponent implements OnInit, OnDestroy {
  /** To hold images coming from API call  */
  public images: ContentItem<ImageGalleryData>[];
  /** To hold content coming from AEM call */
  public pdpStaticContent: ConfigData;
  /**
   * Unsubscribes from all the subscriptions
   */
  private ngUnsubscribe: Subject<void>;

  constructor(private contentProviderService: ContentProviderService) {
    this.ngUnsubscribe = new Subject<void>();
  }

  /**
   * Component life cycle hook to initialize the component
   * @returns void
   */
  public ngOnInit(): void {
    this.getProductDetailsContent()
      .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
      .subscribe((content: ConfigData) => {
        this.pdpStaticContent = content;
      });
  }

  /**
   * Sets the data for the specified component. This is required as the AEM module will use this method to populate
   * component data
   * @param data Input data for AEM component
   * @returns void
   */
  public setData(data: ContainerData): void {
    if (data && data.items) {
      this.images = data.items;
    }
  }

  /**
   * gets the pdp static content coming from AEM call
   * @returns Observable<object>
   */
  public getProductDetailsContent(): Observable<object> {
    return this.contentProviderService
      .getContentData(CONTENT_ENDPOINT.PRODUCT_DETAILS)
      .pipe(
        map((contentData: ConfigData) =>
          this.contentProviderService.getConfigurableData(contentData)
        )
      );
  }

  /**
   * Component life cycle hook to destroy the component
   * @returns void
   */
  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
